import { useEffect, useRef } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { feedIdFromRouteId, parseRequestRouteId } from '@shared/flow/requests';
import { FLOW_REQUEST_URL_PARAM } from '../../../lib/urlParams';
import { useEnrollQueue } from './EnrollQueueContext';

/**
 * `/flow/requests/:requestId` — the bare, shareable request deep link (U13,
 * plan D2).
 *
 * This is the URL `terra_signals.source_link`, the detail view's "Copy link"
 * and anything a rep pastes into Slack all speak: colon-free namespaced ROUTE
 * ids (`ei-<enrollment item id>` / `ex-<execution uuid>`, parsed by the ONE
 * shared module `src/shared/flow/requests.ts` so SQL, the API and the router
 * can never disagree).
 *
 * Shape mirrors `BareArtifactRoute` (AppRouter.tsx): a redirect-on-load shim,
 * not a page. The request detail itself has no page of its own — it lives in
 * the shell-level enroll-queue dock — so this route's whole job is to hand
 * the id to the dock and then get out of the browser history's way
 * (`replace`), landing on the canonical in-app shape
 * `/flow/command-center?request=<route id>`.
 *
 * Landing target is `/flow/command-center`, NOT `/flow`: the Flow shell's inner
 * `<Navigate to="command-center">` index redirect drops the query string, so
 * navigating to `/flow?request=…` would silently lose the very id this route
 * exists to carry. From there `useDockUrlSync`'s READ effect is what
 * keeps the detail open across reloads of that URL; the `openRequestDetail`
 * call below is what makes the FIRST paint immediate rather than waiting a
 * navigation for the param to be read back.
 *
 * A malformed id (`ei-x`, the legacy `enrollment_item:42` colon spelling, a
 * truncated uuid) renders an in-place not-found panel and does NOT redirect —
 * bouncing a bad link to Command Center would look like the link "worked" and
 * leave the rep hunting for a request that was never named.
 */
export function RequestDeepLinkRoute() {
  const { requestId } = useParams<{ requestId: string }>();
  const param = requestId ?? '';
  const parsed = parseRequestRouteId(param);
  const { openRequestDetail } = useEnrollQueue();

  // Latest-ref for the context callback: `openRequestDetail` is a new closure
  // whenever the provider's memo re-runs, and listing it as a dependency
  // would re-fire this effect (re-opening a detail the rep may have just
  // backed out of) on renders where the URL id never changed. Same pattern,
  // for the same reason, as the chat READ effect in `useDockUrlSync.ts`.
  const openRequestDetailRef = useRef(openRequestDetail);
  openRequestDetailRef.current = openRequestDetail;

  // Which id this component instance has already handed to the dock.
  //
  // StrictMode (CLAUDE.md "StrictMode-Resilient Effects"): the dev
  // double-invoke is `setup → cleanup → setup` on the SAME instance, so the
  // ref deliberately keeps its value across the pair — that is what makes the
  // open happen exactly once instead of twice. There is no cleanup writing to
  // it, so it can never be left stuck: a real remount constructs a fresh ref
  // (initialized here, at construction, not in a cleanup), and a param change
  // within one mount is caught by the `!== param` comparison rather than a
  // boolean that only a cleanup could reset.
  const openedForRef = useRef<string | null>(null);

  useEffect(() => {
    // Re-parsed inside the effect rather than closing over the render-derived
    // `parsed`: that value is a NEW object every render, so depending on it
    // would re-fire the effect on unrelated re-renders. The raw param string
    // is the real identity of "which request this URL names".
    const target = parseRequestRouteId(param);
    if (target === null) return;
    if (openedForRef.current === param) return;
    openedForRef.current = param;
    openRequestDetailRef.current(feedIdFromRouteId(target));
  }, [param]);

  if (parsed === null) {
    return (
      // Same size/weight/tone as RequestDetail's own not-found copy and its
      // Back link. The semantic tokens are theme-aware in index.css, so no
      // `dark:` raw-palette override is needed here (the design-system lint
      // flags those; RequestDetail's predate the rule).
      <div className="flex flex-col items-center justify-center h-64 px-4 text-center">
        <div
          data-testid="request-deep-link-not-found"
          className="text-[12.5px] text-content-tertiary"
        >
          This request link is not valid — the id <span className="font-mono">{param}</span> is not a
          request we recognize.
        </div>
        <Link
          to="/flow/command-center"
          className="mt-3 text-[11.5px] font-semibold text-content-action hover:underline"
        >
          Go to Flow
        </Link>
      </div>
    );
  }

  return (
    <Navigate
      to={`/flow/command-center?${FLOW_REQUEST_URL_PARAM}=${encodeURIComponent(param)}`}
      replace
    />
  );
}
